import { DatePipe, formatDate } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { BehaviorSubject, Subscription } from "rxjs";
import { map, share, tap } from "rxjs/operators";
import { LunaEntity } from "../../../../builder/models/entity.model";
import { EntityBuilderService } from "../../../../builder/services/entity-builder.service";
import { CommitProvisionedFileWithKeywordsRequest, OnBaseDocumentGroup, OnBaseDocumentType, OnBaseKeywordMappingSearchSpec, OnBaseKeywordValue, OnBaseMappedKeyword, OnBaseProgramArea } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import { Organization, OrganizationStub } from "../../../../common/models/organization.model";
import { Well } from "../../../../common/models/well.model";
import { DocumentManagementService, IUploadFile } from "../../../../common/services/document-management.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { OnBaseIntegrationAndMappingService } from "../../../../common/services/onbase-integration-mapping.service";
import { OrganizationService } from "../../../../common/services/organization.service";
import { WellService } from "../../../../common/services/well.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { AuthService } from "../../../../services/auth.service";
import { UploadViewFilesComponent } from "../../../../common/components/fileUpload/upload-view-files.component";
import { NgSelectComponent } from "@ng-select/ng-select";
import { getPropertyValue } from "../../../../common/models/data-manipulation-functions";

@Component({
    selector: 'onbase-interface-upload-component',
    templateUrl: './onbase-interface-upload-component.html',
    styleUrls: ['./onbase-interface-upload-component.scss'],
    standalone: false
})
export class OnBaseInterfaceUploadComponent implements OnInit, OnDestroy {

    @ViewChild(UploadViewFilesComponent) public _uploadViewFilesElement: UploadViewFilesComponent;
    @ViewChild("nameSearchSelect") public nameSearchSelect: NgSelectComponent;

    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);

    selectedDocumentGroupProgramAreaId?: number = 0;
    selectedDocumentGroup: OnBaseDocumentGroup = { id: -1, name: "-- Select a Document Group --" };
    selectedDocumentType: OnBaseDocumentType = { id: -1, name: "-- Select a Document Type --" };
    documentGroups: OnBaseDocumentGroup[] = [{ id: -1, name: "-- Select a Document Group --" }];
    documentTypes: OnBaseDocumentType[] = [{ id: -1, name: "-- Select a Document Type --" }];
    activeEntities: LunaEntity[] = [{ id: -1, entityName: "-- Select an Entity if applicable. --" } as unknown as LunaEntity];
    programAreas: OnBaseProgramArea[] = [];
    mappedKeyWords: OnBaseMappedKeyword[] = [];
    mappedKeyWordsWithEntityMaps: OnBaseMappedKeyword[] = [];
    mappedKeyWordsRequired: OnBaseMappedKeyword[] = [];
    mappedKeyWordsOptional: OnBaseMappedKeyword[] = [];
    disabledKeywords: string[] = [];
    keywordsLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public docGroupToTypeDictionary: Map<string, OnBaseDocumentType[]> = new Map<string, OnBaseDocumentType[]>();
    public docTypeToMappedKeywordDictionary: Map<string, OnBaseMappedKeyword[]> = new Map<string, OnBaseMappedKeyword[]>();

    loadingDocTypes = false;
    loadingKeywordMappings = false;

    header: SubHeader = new SubHeader({ title: "OnBase Document Upload", buttons: [] });

    subscription: Subscription = new Subscription();

    apiToSearchFor: string = '';
    partialOrgNameToSearchFor: string = '';
    ricsOrgIdToSearchFor: string = '';
    legacyOrgIdToSearchFor: string = '';

    matchingOrgs: OrganizationStub[] = [];
    relatedWell: Well = null;
    relatedOrg: Organization = null;

    uploadedFiles: IUploadFile[] = [];
    uploadComplete: false;

    mappedKeywordsForm: FormGroup;

    constructor(public router: Router, private _subheaderSvc: SubHeaderService, private loader: LoaderService, private _documentService: DocumentManagementService, private _entityService: EntityBuilderService,
        private _mappingService: OnBaseIntegrationAndMappingService, private _dialogService: DynamicDialogService, public auth: AuthService, public wellService: WellService, public orgService: OrganizationService,
        public datepipe: DatePipe) {

    }

    ngOnInit() {
        this._subheaderSvc.updateSubHeader(this.header);

        this.createFormFields();

        let documentGroupsObs$ = this._documentService.getAllMappedDocumentGroups().pipe(tap((documentGroups: OnBaseDocumentGroup[]) => {
            this.documentGroups = this.documentGroups.concat(documentGroups);
        })).subscribe(() => { },
            (error) => {
                console.error(error);
                this.handleError(error, "An error occurred when trying to fetch the available Document Groups from OnBase.");
            }
        );
        this.subscription.add(documentGroupsObs$);

        let programAreasObs$ = this._documentService.getAllProgramAreas().pipe(tap((programAreas: OnBaseProgramArea[]) => {
            this.programAreas = programAreas;
        })).subscribe({
            next: (programAreas) => {
                //If there is a selected group already, and a keyword for the program area has been added to formgroup, set it
                if (this.selectedDocumentGroup.name != "--Select a Document Group--" && this.mappedKeywordsForm.contains("DOGRM - Program Area")) {
                    let programAreaName = programAreas.filter(a => a.id == this.selectedDocumentGroupProgramAreaId);
                    this.mappedKeywordsForm.controls["DOGRM - Program Area"].setValue(programAreaName);
                    this.disabledKeywords.push("DOGRM - Program Area");
                }
            },
            error: (error) => {
                console.error(error);
                this.handleError(error, "An error occurred when trying to fetch the available Program Areas.");
            }
        });
        this.subscription.add(programAreasObs$);
       
        let activeEntitiesObs$ = this._entityService.getActive().pipe(tap((activeEntities: LunaEntity[]) => {
            this.activeEntities = this.activeEntities.concat(activeEntities);
        })).subscribe({
            next: () => { },
            error: (error) => {
                console.error(error);
                this.handleError(error, "An error occurred when trying to fetch the available entities.");
            }
        });
        this.subscription.add(activeEntitiesObs$);

        this.mappedKeywordsForm = new FormGroup({});
        this.mappedKeywordsForm.addControl("uploadedFile", new FormControl([]));
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    docGroupSelected(event: any) {
        this.mappedKeyWords = [];
        this.mappedKeyWordsWithEntityMaps = [];
        this.mappedKeyWordsRequired = [];
        this.mappedKeyWordsOptional = [];
        this.selectedDocumentGroupProgramAreaId = 0;
        this.selectedDocumentType = { id: -1, name: "-- Select a Document Type --" };
        this.documentTypes = [{ id: -1, name: "-- Select a Document Type --" }];
        this.selectedDocumentGroupProgramAreaId = this.selectedDocumentGroup.programAreaId;
        this.createFormFields();
        if (this.selectedDocumentGroup && this.selectedDocumentGroup.name != "-- Select a Document Group --" && !this.docGroupToTypeDictionary.has(this.selectedDocumentGroup.name)) {
            this.loadingDocTypes = true;
            let documentGroupTypesObs$ = this._mappingService.getDocumentTypesByDocumentGroup(this.selectedDocumentGroup.name).pipe(tap((documentTypes: OnBaseDocumentType[]) => {
                documentTypes.unshift({ id: -1, name: "-- Select a Document Type --" })
                this.docGroupToTypeDictionary.set(this.selectedDocumentGroup.name, documentTypes);
                this.loadingDocTypes = false;
            })).subscribe({
                next: () => { },
                error: (error) => {
                    console.error(error);
                    this.handleError(error, "An error occurred when trying to fetch the related Document Types.");
                    this.loadingDocTypes = false;
                }
            }
            );
            this.subscription.add(documentGroupTypesObs$);
        }
    }

    docTypeSelected(event: any) {
        this.mappedKeyWords = [];
        this.mappedKeyWordsWithEntityMaps = [];
        this.mappedKeyWordsRequired = [];
        this.mappedKeyWordsOptional = [];
        this.createFormFields();

        if (this.selectedDocumentType && this.selectedDocumentType.name != "-- Select a Document Type --" && !this.docTypeToMappedKeywordDictionary.has(this.selectedDocumentType.name)) {
            this.loadingKeywordMappings = true;
            this.keywordsLoaded.next(false);
            let documentTypeKeywordsObs$ = this._documentService.findKeywordMappings({ documentGroupName: this.selectedDocumentGroup.name, documentTypeName: this.selectedDocumentType.name } as OnBaseKeywordMappingSearchSpec)
                .pipe(tap((mappedKeywords: OnBaseMappedKeyword[]) => {
                    //Strip out the file name one, as it will be set per file as they are uploaded within the DocumentManagementLogic Repository's CommitDocument method
					mappedKeywords = mappedKeywords.filter(a => a.name != "DOGRM - Legacy File Name");
                    this.docTypeToMappedKeywordDictionary.set(this.selectedDocumentType.name, mappedKeywords);
                    this.mappedKeyWords = this.docTypeToMappedKeywordDictionary.get(this.selectedDocumentType.name) ?? [];
                    this.buildFilteredKeywordLists();
                    this.createFormFields();
                    this.loadingKeywordMappings = false;
                    this.keywordsLoaded.next(true);
                })).subscribe({
                    next: () => { },
                    error: (error) => {
                        console.error(error);
                        this.handleError(error, "An error occurred when trying to fetch the related Keywords.");
                        this.loadingKeywordMappings = false;
                    }
                });
            this.subscription.add(documentTypeKeywordsObs$);
        }
        else if (this.selectedDocumentType && this.docTypeToMappedKeywordDictionary.has(this.selectedDocumentType.name)) {

            this.mappedKeyWords = this.docTypeToMappedKeywordDictionary.get(this.selectedDocumentType.name) ?? [];
            this.buildFilteredKeywordLists();
            this.createFormFields();
        }
    }

    filteredDocTypes() {
        //If a document group has been selected, filter down the available doc types
        if (this.selectedDocumentGroup && this.docGroupToTypeDictionary.has(this.selectedDocumentGroup.name)) {
            this.documentTypes = this.docGroupToTypeDictionary.get(this.selectedDocumentGroup.name) ?? [];
        }

        return this.documentTypes;
    }

    validDocumentTypeSelected(): boolean {
        return this.selectedDocumentGroup && this.selectedDocumentGroup.name != "-- Select a Document Group --"
                && this.selectedDocumentType && this.selectedDocumentType.name != "-- Select a Document Type --";
    }

    handleError(error: any, message: string): void {
        this.errorDialog.toggleDialog(true);
        this.errorDialog.message = message;
        this._dialogService.updateDialog(this.errorDialog);
    }

    wipeExistingEntity() {
        this.relatedWell = null;
        this.relatedOrg = null;
    }

    rebuildFormFields() {
        this.buildFilteredKeywordLists();
        this.createFormFields();
    }

    getWell() {
        this.loader.toggleLoader(true);
        let wellObs$ = this.wellService.getWellById(this.apiToSearchFor).pipe(tap((well: Well) => {
            this.wipeExistingEntity();
            this.relatedWell = well;
            this.rebuildFormFields();
            this.loader.toggleLoader(false);
        })).subscribe({
            next: () => { },
            error: (error) => {
                this.wipeExistingEntity();
                this.rebuildFormFields();
                console.error(error);
                this.handleError(error, "No well with that API was found.");
                this.loader.toggleLoader(false);
            }
        });
        this.subscription.add(wellObs$);
    }

	//may need to rework this page to preload organizations so the ng-select doesn't trigger server calls at all
    getOrgByPartialName() {
        this.loader.toggleLoader(true);
		let matchingOrgsObs$ = this.orgService.getOrganizationStubsByPartialName(this.partialOrgNameToSearchFor).pipe(
			tap((orgStubs: OrganizationStub[]) => {
				this.wipeExistingEntity();
				this.matchingOrgs = orgStubs;
				//this.nameSearchSelect.searchTerm = this.partialOrgNameToSearchFor;
				this.nameSearchSelect.open();
				this.rebuildFormFields();
				this.loader.toggleLoader(false);
        })).subscribe({
            next: () => { },
            error: (error) => {
                this.wipeExistingEntity();
                this.rebuildFormFields();

                console.error(error);
                this.handleError(error, "No organization with a similar name was found.");
                this.loader.toggleLoader(false);
            }
        });
        this.subscription.add(matchingOrgsObs$);
    }

    getOrgFromRicsOrgId() {
        this.loader.toggleLoader(true);
        let orgObs$ = this.orgService.getOrganization(this.ricsOrgIdToSearchFor).pipe(tap((org: Organization) => {
            this.wipeExistingEntity();
            this.relatedOrg = org;
            this.rebuildFormFields();
            this.loader.toggleLoader(false);
        })).subscribe({
            next: () => { },
            error: (error) => {
                this.wipeExistingEntity();
                this.rebuildFormFields();

                console.error(error);
                this.handleError(error, "No organization with that RICS organization id was found.");
                this.loader.toggleLoader(false);
            }
        });
        this.subscription.add(orgObs$);
    }

    getOrgFromRbdmsOrgId() {
        this.loader.toggleLoader(true);
        let orgObs$ = this.orgService.getOrganization(this.legacyOrgIdToSearchFor).pipe(tap((org: Organization) => {
            this.wipeExistingEntity();
            this.relatedOrg = org;
            this.rebuildFormFields();
            this.loader.toggleLoader(false);
        })).subscribe({
            next: () => { },
            error: (error) => {
                this.wipeExistingEntity();
                this.rebuildFormFields();

                console.error(error);
                this.handleError(error, "No organization with that RBDMS organization id was found.");
                this.loader.toggleLoader(false);
            }
        });
        this.subscription.add(orgObs$);

    }

    getRelevantEntity(): any {
        if (this.relatedWell != null)
            return this.relatedWell;

        if (this.relatedOrg != null)
            return this.relatedOrg;

        else
            return null;
    }

    onKeywordValueUpdated() {
        this.validateForm();
    }

    createFormFields(): void {
        let relevantEntity = this.getRelevantEntity();
        this.disabledKeywords = [];
        //Wipe out everything
        this.mappedKeywordsForm = new FormGroup({});

        if (this.mappedKeyWords.length > 0) {
            this.mappedKeyWords.forEach(value => {
                this.buildEntityMappedFormControl(value, relevantEntity);
            })
        }

        //Add in file upload control
        this.mappedKeywordsForm.addControl("uploadedFile", new FormControl([], Validators.required));
        this.validateForm();
    }

    buildEntityMappedFormControl(keyword: OnBaseMappedKeyword, relevantEntity: any) {
        let entityValue = "";
        if (keyword.mappedEntities && keyword.mappedEntities.length > 0 && relevantEntity != null) {
            let relevantEntityMaps = keyword.mappedEntities.filter(a => a.entityId == relevantEntity.documentType);

			if (relevantEntityMaps.length > 0) {
				entityValue = getPropertyValue(relevantEntity, relevantEntityMaps[0].entityFieldPath, "");
            }
        }
        //if the control exists, and there is an entity value that differs from an empty string, set it
        if (this.mappedKeywordsForm.contains(keyword.name) && entityValue != "") {
            if (keyword.allowMultiSelect)
                this.mappedKeywordsForm.controls[keyword.name].setValue([entityValue]);
            else
                this.mappedKeywordsForm.controls[keyword.name].setValue(entityValue);

            this.mappedKeywordsForm.controls[keyword.name].disable();
            this.disabledKeywords.push(keyword.name);
            this.mappedKeywordsForm.controls[keyword.name].disable();
        }
        else {
            //A few keywords will always be set automatically handle those here
            switch (keyword.name) {
                case "DOGRM - Program Area": if (this.selectedDocumentGroupProgramAreaId) {
                    entityValue = this.programAreas.filter(a => a.id == this.selectedDocumentGroupProgramAreaId)[0].name;
                    this.disabledKeywords.push(keyword.name);
                    }
                    break; 
                case "DOGRM - Date Uploaded": 
                    entityValue = this.datepipe.transform(new Date(), 'yyyy-MM-dd');
                    this.disabledKeywords.push(keyword.name)
                    break;
                default: {
                    break;
                }
            }
            //If an entityValue was provided, the keyword isn't already on list, add it
            if (entityValue != "" && !(this.disabledKeywords.filter(a => a == keyword.name).length > 0)) {
                this.disabledKeywords.push(keyword.name);
			}

			if (entityValue == "" && keyword.dropdownValues != null && keyword.dropdownValues.length > 0) {
				let defaultValue = keyword.dropdownValues.find(d => d.isDefault == true);
				if (defaultValue != null) {
					entityValue = defaultValue.value;
				}
			}

            //else add control
			if (keyword.allowMultiSelect && entityValue != "" && entityValue != null && entityValue != undefined) {
				this.mappedKeywordsForm.addControl('' + keyword.name + '', new FormControl([entityValue], keyword.isRequired ? Validators.required : null));
			}
			else {
				this.mappedKeywordsForm.addControl('' + keyword.name + '', new FormControl(entityValue, keyword.isRequired ? Validators.required : null));
			}

            if (this.disabledKeywords.filter(a => a == keyword.name).length > 0)
                this.mappedKeywordsForm.controls[keyword.name].disable();
        }
    }

    buildFilteredKeywordLists() {
        let entity = this.getRelevantEntity();

        //ALERT: This holds true for the org and well, where their entity id matches their partitionKey in Cosmos
        //Need to keep an eye on this as we potentially do further enhancements.
        if (entity) {
            this.mappedKeyWordsWithEntityMaps = this.mappedKeyWords.filter(a => a.mappedEntities && a.mappedEntities.length > 0 && a.mappedEntities.filter(a => a.entityId == entity.documentType).length > 0);
        }
        else
            this.mappedKeyWordsWithEntityMaps = [];

        //Fill in the required and optional keywords with any relevant ones not already in entity keywordMap
        this.mappedKeyWordsRequired = this.mappedKeyWords.filter(a => a.isRequired && !(this.mappedKeyWordsWithEntityMaps.filter(b => b.name == a.name).length > 0));
        this.mappedKeyWordsOptional = this.mappedKeyWords.filter(a => !a.isRequired && !(this.mappedKeyWordsWithEntityMaps.filter(b => b.name == a.name).length > 0));
    }

    deleteFile(file: IUploadFile): void {
        let fileDel$ = this._documentService.abandon(file).pipe()
            .subscribe({
                next: (deleted) => {
                    let index = this.uploadedFiles.findIndex(f => f.fileName === file.fileName);
                    if (index !== -1) {
                        this.uploadedFiles.splice(index, 1);
                    }

                    //Now make sure the file got removed from the upload-view-files component too
                    this._uploadViewFilesElement.removeFile(file);
                },
                error: (error) => { console.error(error); }
            }
                
        );
        this.subscription.add(fileDel$);
    } 

    savedFiles(file: IUploadFile): void {
        this.uploadedFiles.push(file);
        this.validateUpload();
    }

    validateUpload(): void {
        let upload = this.mappedKeywordsForm.get("uploadedFile");
        upload.setValue(this.uploadedFiles);
        upload.markAsTouched();
        upload.markAsDirty();
        upload.updateValueAndValidity();
    }

    get hasFileListing(): boolean {
        return this.uploadedFiles && this.uploadedFiles.length > 0;
    }

    validateForm(): boolean {
        this.validateUpload();
        this.mappedKeywordsForm.updateValueAndValidity();
        return this.mappedKeywordsForm.valid;
    }

    onSubmitClick() {
        let provisionedFileRequests = this.buildCommitRequest();
        this.loader.toggleLoader(true);
        let commitFilesObs$ = this._documentService.commitProvisionedFilesToOnBase(provisionedFileRequests).subscribe({
            next: (response) => {
                this.loader.toggleLoader(false);
                this.onReturnClick();
            },
            error: (error) => {
                console.error(error);
                this.handleError(error, "An error occurred while trying to upload the file(s).");
                this.loader.toggleLoader(false);
            }
        }
        );
        this.subscription.add(commitFilesObs$);
    }

    keywordIsDisabled(keywordName: string): boolean {
        return (this.disabledKeywords.filter(a => a == keywordName).length > 0);
    }

    onReturnClick() {
        this.router.navigate(['/home']);
    }

    onSubmitAndAddAnotherClick() {
        let provisionedFileRequests = this.buildCommitRequest();
        this.loader.toggleLoader(true);
        let commitFilesObs$ = this._documentService.commitProvisionedFilesToOnBase(provisionedFileRequests).subscribe({
            next: (response) => {
                this.loader.toggleLoader(false);

                //Just reload the page
                let currentUrl = this.router.url;
                this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                    this.router.navigate([currentUrl]);
                });
            },
            error: (error) => {
                console.error(error);
                this.handleError(error, "An error occurred while trying to upload the file(s).");
                this.loader.toggleLoader(false);
            }
        });
        this.subscription.add(commitFilesObs$);
    }

    buildCommitRequest(): CommitProvisionedFileWithKeywordsRequest[] {
        let success = false;
        let isFormValid = this.validateForm();
        let keywordValues: OnBaseKeywordValue[] = [];
        let provisionedFileRequests: CommitProvisionedFileWithKeywordsRequest[] = [];

        //BuildKeyValues From the form
        Object.keys(this.mappedKeywordsForm.controls).forEach(key => {
            let controlInstance = this.mappedKeywordsForm.controls[key];
            let keyword = this.mappedKeyWords.filter(a => a.name == key)[0];
            if (controlInstance.value != null && controlInstance.value != "" && keyword) {
                if (!keyword.allowMultiSelect)
                    keywordValues.push({ /*keywordTypeId: keyword.id,*/ keywordTypeName: keyword.name, keywordValue: controlInstance.value } as OnBaseKeywordValue);
                else if (controlInstance.value.length > 0) {
                    for (let x = 0; x < controlInstance.value.length; x++) {
                        keywordValues.push({ /*keywordTypeId: keyword.id,*/ keywordTypeName: keyword.name, keywordValue: controlInstance.value[x] } as OnBaseKeywordValue);
                    }
                }

            }
        });


        if (isFormValid && this.uploadedFiles.filter(a => a.completed).length == this.uploadedFiles.length) {
            for (let x = 0; x < this.uploadedFiles.length; x++) {
                let request = {
                    provisionedFile: this.uploadedFiles[x],
                    documentTypeName: this.selectedDocumentType.name,
                    keywordValues: keywordValues
                } as CommitProvisionedFileWithKeywordsRequest;
                provisionedFileRequests.push(request);
            }
        }

        return provisionedFileRequests;
    }


    onPartialOrgNameChange($event) {
        if ($event == undefined || $event == null)
            this.partialOrgNameToSearchFor = "";

        this.relatedOrg = $event;
        this.buildFilteredKeywordLists();
        this.createFormFields();
    }

    setPartialOrgNameToSearchFor($event) {
        this.partialOrgNameToSearchFor = $event.term;
    }
}